<template>
  <Container>
    <template #header>
      <Header :menu="$page.props.main_menu" v-if="$page.props.auth.user" />
    </template>
    <template #default>
      <div>
        <Breadcrumbs
          :breadcrumbs="[
            { name: 'Dashboard', href: route('dashboard') },
            { name: 'Berita', href: route('berita.index') },
          ]"
        />

        <!-- Header Section -->
        <div class="mb-4 sm:mb-6">
          <div
            class="flex flex-col sm:flex-row sm:items-center sm:justify-between"
          >
            <div>
              <h3 class="text-lg font-semibold text-gray-900">Daftar Berita</h3>
              <p class="mt-1 text-sm text-gray-600">
                Kelola berita dan artikel sistem
              </p>
            </div>
            <div class="mt-4 sm:mt-0">
              <Link
                v-if="$page.props.auth.user && $page.props.can.berita_create"
                :href="route('berita.create')"
                class="inline-flex items-center px-4 py-2.5 text-sm font-semibold text-white bg-indigo-600 border border-transparent rounded-lg shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors duration-200"
              >
                <i class="mr-2 fa-solid fa-plus"></i>
                Tambah Berita
              </Link>
            </div>
          </div>
        </div>

        <!-- Search Input -->
        <div class="relative mb-4">
          <div
            class="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none"
          >
            <MagnifyingGlassVue class="w-4 h-4 text-gray-400" />
          </div>
          <input
            v-model="search"
            type="text"
            autocomplete="off"
            placeholder="Cari berita..."
            id="search"
            class="block w-full px-3 py-2.5 pl-10 text-sm text-gray-900 bg-white border border-gray-300 rounded-lg shadow-sm placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 hover:border-gray-400 transition-colors duration-200"
          />
        </div>

        <!-- List -->
        <div
          class="overflow-hidden bg-white border border-gray-200 shadow-sm rounded-xl"
        >
          <div class="overflow-x-auto">
            <table class="min-w-full divide-y divide-gray-200">
              <thead class="bg-gray-50">
                <tr>
                  <th
                    scope="col"
                    class="py-3.5 pl-4 pr-3 text-left text-xs font-semibold tracking-wide text-gray-500 uppercase sm:pl-6 w-12"
                  >
                    #
                  </th>
                  <th
                    scope="col"
                    class="px-3 py-3.5 text-left text-xs font-semibold tracking-wide text-gray-500 uppercase min-w-[260px]"
                  >
                    Berita
                  </th>
                  <th
                    scope="col"
                    class="px-3 py-3.5 text-left text-xs font-semibold tracking-wide text-gray-500 uppercase hidden lg:table-cell min-w-[140px]"
                  >
                    Tags
                  </th>
                  <th
                    scope="col"
                    class="px-3 py-3.5 text-left text-xs font-semibold tracking-wide text-gray-500 uppercase hidden md:table-cell whitespace-nowrap"
                  >
                    Tanggal
                  </th>
                  <th
                    scope="col"
                    class="px-3 py-3.5 text-left text-xs font-semibold tracking-wide text-gray-500 uppercase"
                  >
                    Status
                  </th>
                  <th scope="col" class="relative py-3.5 pl-3 pr-4 sm:pr-6">
                    <span class="sr-only">Aksi</span>
                  </th>
                </tr>
              </thead>
              <tbody class="bg-white divide-y divide-gray-100">
                <tr
                  v-for="(newsItem, index) in news.data"
                  :key="newsItem.id"
                  class="transition-colors duration-150 hover:bg-gray-50"
                >
                  <td
                    class="py-4 pl-4 pr-3 text-sm text-gray-400 whitespace-nowrap sm:pl-6"
                  >
                    {{ getRowNumber(index) }}
                  </td>

                  <!-- Berita: thumbnail + title + excerpt + mobile meta -->
                  <td class="px-3 py-4">
                    <div class="flex items-start gap-3">
                      <img
                        v-if="firstImage(newsItem.image)"
                        :src="firstImage(newsItem.image)"
                        alt="Gambar berita"
                        class="object-cover rounded-lg w-14 h-14 ring-1 ring-gray-200 shrink-0"
                      />
                      <div
                        v-else
                        class="flex items-center justify-center text-gray-400 bg-gray-100 rounded-lg w-14 h-14 shrink-0"
                      >
                        <i class="fa-regular fa-image"></i>
                      </div>

                      <div class="min-w-0">
                        <div
                          class="font-semibold text-gray-900 line-clamp-2"
                        >
                          {{ newsItem.title }}
                        </div>
                        <p class="mt-0.5 text-xs text-gray-500 line-clamp-1">
                          {{ excerpt(newsItem.content) }}
                        </p>

                        <!-- Mobile meta -->
                        <div
                          class="flex flex-wrap items-center gap-2 mt-2 md:hidden"
                        >
                          <span
                            :class="
                              isPublished(newsItem.published)
                                ? 'bg-green-100 text-green-700'
                                : 'bg-amber-100 text-amber-700'
                            "
                            class="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full"
                          >
                            {{
                              isPublished(newsItem.published)
                                ? "Publish"
                                : "Draft"
                            }}
                          </span>
                          <span class="text-xs text-gray-400">
                            {{ newsItem.publish_date }}
                          </span>
                        </div>
                      </div>
                    </div>
                  </td>

                  <!-- Tags -->
                  <td class="hidden px-3 py-4 align-top lg:table-cell">
                    <div
                      v-if="newsItem.tags && newsItem.tags.length"
                      class="flex flex-wrap gap-1"
                    >
                      <span
                        v-for="tag in newsItem.tags.slice(0, 2)"
                        :key="tag"
                        class="inline-flex items-center px-2 py-0.5 text-xs font-medium text-indigo-700 rounded-full bg-indigo-50"
                      >
                        {{ tag }}
                      </span>
                      <span
                        v-if="newsItem.tags.length > 2"
                        class="inline-flex items-center px-2 py-0.5 text-xs font-medium text-gray-600 bg-gray-100 rounded-full"
                      >
                        +{{ newsItem.tags.length - 2 }}
                      </span>
                    </div>
                    <span v-else class="text-xs text-gray-400">—</span>
                  </td>

                  <!-- Tanggal -->
                  <td
                    class="hidden px-3 py-4 text-sm text-gray-600 align-top md:table-cell whitespace-nowrap"
                  >
                    {{ newsItem.publish_date }}
                  </td>

                  <!-- Status -->
                  <td class="px-3 py-4 align-top">
                    <span
                      :class="
                        isPublished(newsItem.published)
                          ? 'bg-green-100 text-green-700'
                          : 'bg-amber-100 text-amber-700'
                      "
                      class="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-full whitespace-nowrap"
                    >
                      <span
                        :class="
                          isPublished(newsItem.published)
                            ? 'bg-green-500'
                            : 'bg-amber-500'
                        "
                        class="w-1.5 h-1.5 rounded-full"
                      ></span>
                      {{ isPublished(newsItem.published) ? "Publish" : "Draft" }}
                    </span>
                  </td>

                  <!-- Aksi -->
                  <td
                    class="py-4 pl-3 pr-4 align-top text-right sm:pr-6 whitespace-nowrap"
                  >
                    <div class="flex items-center justify-end gap-2">
                      <Link
                        v-if="$page.props.can.berita_show"
                        :href="route('berita.show', newsItem.idnews)"
                        class="inline-flex items-center px-2 py-1 text-xs font-medium text-indigo-600 transition-colors duration-150 rounded-md bg-indigo-50 hover:bg-indigo-100 sm:px-2.5 sm:py-1.5"
                        title="Edit Berita"
                      >
                        <i class="w-3 h-3 fa-solid fa-pen-to-square"></i>
                        <span class="hidden ml-1 sm:inline">Edit</span>
                      </Link>
                      <button
                        v-if="$page.props.can.berita_delete"
                        @click="deleteNews(newsItem.idnews)"
                        class="inline-flex items-center px-2 py-1 text-xs font-medium text-red-600 transition-colors duration-150 rounded-md bg-red-50 hover:bg-red-100 sm:px-2.5 sm:py-1.5"
                        title="Hapus Berita"
                      >
                        <i class="w-3 h-3 fa-solid fa-trash"></i>
                        <span class="hidden ml-1 sm:inline">Hapus</span>
                      </button>
                    </div>
                  </td>
                </tr>

                <!-- Empty state -->
                <tr v-if="!news.data || news.data.length === 0">
                  <td colspan="6" class="px-3 py-16 text-center">
                    <i
                      class="mb-3 text-3xl text-gray-300 fa-regular fa-newspaper"
                    ></i>
                    <p class="text-sm font-medium text-gray-900">
                      Belum ada berita
                    </p>
                    <p class="mt-1 text-sm text-gray-500">
                      {{
                        search
                          ? "Tidak ada berita yang cocok dengan pencarian."
                          : "Mulai dengan menambahkan berita baru."
                      }}
                    </p>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <!-- Pagination -->
        <Pagination :data="news" :updatedPageNumber="updatedPageNumber" />
      </div>
    </template>
  </Container>
</template>
<script setup>
import { ref, onMounted, computed, watch } from "vue";
import Container from "@/Layouts/Container.vue";
import Pagination from "@/Components/Pagination.vue";
import Header from "@/Components/Header.vue";
import Breadcrumbs from "@/Components/Breadcrumbs.vue";
import MagnifyingGlassVue from "@/Components/MagnifyingGlass.vue";
import Swal from "sweetalert2";
import { router, useForm, usePage } from "@inertiajs/vue3";

const props = defineProps({
  news: {
    type: Object,
    required: true,
  },
  allTags: {
    type: Array,
    required: true,
  },
});

/**Pagination */
let search = ref(usePage().props.search),
  pageNumber = ref(1);

let newsUrl = computed(() => {
  let url = new URL(route("berita.index"));
  url.searchParams.append("page", pageNumber.value);
  if (search.value) {
    url.searchParams.append("search", search.value);
  }
  return url;
});

const updatedPageNumber = (link) => {
  pageNumber.value = link.url.split("=")[1];
};

// Strip HTML tags and collapse whitespace to produce a clean text excerpt.
const excerpt = (html, max = 120) => {
  const text = (html || "")
    .replace(/<[^>]*>/g, " ")
    .replace(/&nbsp;/g, " ")
    .replace(/\s+/g, " ")
    .trim();
  return text.length > max ? text.slice(0, max).trim() + "…" : text;
};

// `image` is cast to an array; return the first usable URL (or null).
const firstImage = (image) => {
  if (Array.isArray(image)) return image[0] || null;
  if (typeof image === "string" && image) return image;
  return null;
};

const isPublished = (value) => Number(value) === 1;

// Row number calculation with fallback for pagination
const getRowNumber = (index) => {
  const currentPage = props.news.current_page || 1;
  const perPage = props.news.per_page || 10;
  const calculated = (currentPage - 1) * perPage + index + 1;
  return isNaN(calculated) ? index + 1 : calculated;
};

watch(
  () => newsUrl.value,
  (updateNewsUrl) => {
    router.visit(updateNewsUrl, {
      preserveScroll: true,
      preserveState: true,
      replace: true,
    });
  }
);

watch(
  () => search.value,
  (value) => {
    if (value) {
      pageNumber.value = 1;
    }
  }
);
//End of Pagination

// Delete function with improved SweetAlert2
const deleteNews = async (newsId) => {
  const result = await Swal.fire({
    title: "Apakah Anda yakin?",
    text: "Berita akan dihapus permanen!",
    icon: "warning",
    showCancelButton: true,
    confirmButtonColor: "#ef4444",
    cancelButtonColor: "#6b7280",
    confirmButtonText: "Ya, hapus!",
    cancelButtonText: "Batal",
  });

  if (result.isConfirmed) {
    // Show loading
    Swal.fire({
      title: "Menghapus...",
      text: "Sedang menghapus berita",
      allowOutsideClick: false,
      didOpen: () => {
        Swal.showLoading();
      },
    });

    router.delete(route("berita.destroy", newsId), {
      onSuccess: () => {
        Swal.fire({
          title: "Terhapus!",
          text: "Berita berhasil dihapus",
          icon: "success",
          confirmButtonText: "OK",
        });
      },
      onError: () => {
        Swal.fire({
          title: "Error!",
          text: "Terjadi kesalahan saat menghapus berita",
          icon: "error",
          confirmButtonText: "OK",
        });
      },
      preserveScroll: true,
    });
  }
};

function addTag(newTag) {
  const exists =
    form.tags.some((tag) => tag.name === newTag) ||
    props.allTags.some((tag) => tag.name === newTag);

  if (!exists) {
    form.tags.push({ name: newTag });
  }
}

const form = useForm({
  id: null,
  title: "",
  content: "",
  publish_date: "",
  published: "",
  tags: [],
  image: [],
  imagePreview: [],
});

function handleFileChange(e) {
  const files = Array.from(e.target.files);
  form.image = files;
  form.imagePreview = files.map((file) => URL.createObjectURL(file));
}

onMounted(() => {
  const page = usePage();
  if (page.props.flash && page.props.flash.success) {
    Swal.fire({
      title: "Berhasil!",
      text: page.props.flash.success,
      icon: "success",
      confirmButtonText: "OK",
    });
  }
});

const fileInput = ref(null);
const quillRef = ref(null);

const submit = async () => {
  const selectedTags = form.tags.map((tag) => tag.name ?? tag);
  const formData = new FormData();
  formData.append("title", form.title);
  formData.append("content", form.content);
  formData.append("publish_date", form.publish_date);
  formData.append("published", form.published);

  if (Array.isArray(selectedTags)) {
    selectedTags.forEach((tag) => {
      formData.append("tags[]", tag);
    });
  }

  if (Array.isArray(form.image)) {
    form.image.forEach((file, index) => {
      formData.append(`image[${index}]`, file);
    });
  }

  form.errors = {};

  try {
    const response = await axios.post(route("berita.store"), formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });

    Swal.fire({
      title: "Berhasil!",
      text: "Berita berhasil disimpan",
      icon: "success",
      confirmButtonText: "OK",
    }).then(() => {
      window.location.reload();
      form.reset();
      form.image = [];
      form.imagePreview = [];
      if (fileInput.value) fileInput.value.value = "";
      form.tags = [];
      form.title = "";
      form.content = "";
      form.publish_date = "";
      form.published = "";
      if (quillRef.value) {
        quillRef.value.setHTML && quillRef.value.setHTML("");
        if (quillRef.value.editor && quillRef.value.editor.setContents) {
          quillRef.value.editor.setContents([]);
        }
      }
    });
  } catch (error) {
    if (error.response && error.response.status === 422) {
      form.errors = error.response.data.errors;
    } else {
      Swal.fire({
        title: "Gagal!",
        text: "Terjadi kesalahan saat menyimpan berita",
        icon: "error",
        confirmButtonText: "OK",
      });
    }
  }
};
</script>

<style scoped>
/* Ensure proper text truncation */
:deep(.line-clamp-2) {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
}

:deep(.line-clamp-3) {
  display: -webkit-box;
  -webkit-line-clamp: 3;
  line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* Clean HTML content styling */
:deep(.line-clamp-2 *),
:deep(.line-clamp-3 *) {
  margin: 0 !important;
  padding: 0 !important;
  font-size: inherit !important;
  line-height: inherit !important;
}
</style>
